/**
 * usePixDiscount — reads the admin-editable "desconto PIX" setting.
 *
 * Same caching pattern as useMaxInstallments: exactly ONE fetch per page load,
 * shared across every mounted subscriber. Returns the discount percent (0-50)
 * where 0 means the feature is disabled without any code change.
 */
import { useEffect, useState } from "react";
import { api } from "./api";

let cachedPct = null;
let pending = null;
const subscribers = new Set();

function fetchOnce() {
  if (cachedPct !== null) return Promise.resolve(cachedPct);
  if (pending) return pending;
  pending = api
    .get("/settings/pix-discount")
    .then((r) => {
      const n = Number(r?.data?.pct);
      cachedPct = Number.isFinite(n) && n >= 0 ? Math.min(50, n) : 0;
      subscribers.forEach((fn) => fn(cachedPct));
      return cachedPct;
    })
    .catch(() => {
      cachedPct = 0;
      subscribers.forEach((fn) => fn(0));
      return 0;
    })
    .finally(() => { pending = null; });
  return pending;
}

export function usePixDiscount() {
  const [pct, setPct] = useState(cachedPct ?? 0);
  useEffect(() => {
    subscribers.add(setPct);
    if (cachedPct === null) fetchOnce();
    else setPct(cachedPct);
    return () => { subscribers.delete(setPct); };
  }, []);
  return pct;
}

/** Called after admin saves — force everyone to re-read the latest value. */
export function refreshPixDiscount() {
  cachedPct = null;
  pending = null;
  fetchOnce();
}
